import { useMemo } from "react";
import { Badge } from "@panelgenie/shared-ui";
import type { InvocationRecord } from "../types.js";
import {
  confidenceTone,
  FILTERS,
  identityFieldConfidence,
  type BreakerRow,
  type FilterKey,
  type PanelIdentity,
  type PanelIdentityConfidenceField,
  type Sort,
  type SortKey,
} from "../lib/reads.js";
import { suggestionRawJson } from "../lib/format.js";
import { JsonHighlight } from "./JsonHighlight.js";

/**
 * The right pane (handoff §4). On a clean read it shows every breaker the
 * model returned in a sortable / filterable table; selecting a row opens
 * its verbatim JSON and the parent traces the center pane straight to the
 * tile it was read from (the most-used diagnostic motion, brief §5.3, now
 * fused into selection itself). With no breaker selected it shows the
 * panel-identity card. On a parse failure it shows the verbatim,
 * unterminated model response instead.
 */

export type ReadsPanelProps = {
  readonly rows: readonly BreakerRow[];
  readonly totalReads: number;
  readonly identity: PanelIdentity | null;
  readonly invocation: InvocationRecord;
  readonly parseFailed: boolean;
  readonly selectedBreakerId: string | null;
  readonly onSelectBreaker: (suggestionId: string) => void;
  readonly sort: Sort;
  readonly onSortChange: (key: SortKey) => void;
  readonly filter: FilterKey;
  readonly onFilterChange: (filter: FilterKey) => void;
};

export function ReadsPanel({
  rows,
  totalReads,
  identity,
  invocation,
  parseFailed,
  selectedBreakerId,
  onSelectBreaker,
  sort,
  onSortChange,
  filter,
  onFilterChange,
}: ReadsPanelProps) {
  if (parseFailed) {
    return <ParseFailedPane invocation={invocation} />;
  }

  const selectedRow =
    selectedBreakerId === null
      ? null
      : (rows.find((r) => r.suggestionId === selectedBreakerId) ?? null);

  return (
    <aside
      aria-label="reads-panel"
      className="flex h-full min-h-0 flex-col border-l border-border-1 bg-bg-1"
    >
      {/* Header + filter chips */}
      <div className="flex-none border-b border-border-1 px-4 pb-2.5 pt-3.5">
        <div className="flex items-center justify-between">
          <h2 className="text-[15px] font-semibold text-fg-1">
            Reads <span className="text-fg-3">· {totalReads}</span>
          </h2>
          <FilterIcon />
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {FILTERS.map((f) => {
            const active = f.key === filter;
            return (
              <button
                key={f.key}
                type="button"
                aria-pressed={active}
                onClick={() => onFilterChange(f.key)}
                className={`h-[26px] rounded-md border px-2 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                  active
                    ? "border-accent/40 bg-accent-soft text-accent"
                    : "border-border-1 bg-bg-4 text-fg-2 hover:border-border-2"
                }`}
              >
                {f.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Reads table */}
      <div className="flex min-h-0 flex-[1.1] flex-col overflow-auto border-b border-border-1">
        <table className="w-full border-collapse text-left">
          <thead className="sticky top-0 z-10 bg-bg-1">
            <tr className="border-b border-border-1">
              <SortHeader label="#" k="circuit" sort={sort} onSort={onSortChange} mono />
              <SortHeader label="Pos" k="pos" sort={sort} onSort={onSortChange} mono />
              <SortHeader label="Amp" k="amp" sort={sort} onSort={onSortChange} mono />
              <SortHeader label="Pole" k="pole" sort={sort} onSort={onSortChange} />
              <SortHeader label="Conf" k="conf" sort={sort} onSort={onSortChange} />
              <SortHeader label="Src" k="src" sort={sort} onSort={onSortChange} mono />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-sm text-fg-3">
                  No reads match this filter.
                </td>
              </tr>
            ) : (
              rows.map((r) => {
                const selected = r.suggestionId === selectedBreakerId;
                return (
                  <tr
                    key={r.suggestionId}
                    aria-label={`read ${r.suggestionId}`}
                    aria-selected={selected}
                    onClick={() => onSelectBreaker(r.suggestionId)}
                    className={`cursor-pointer border-b border-border-1/60 transition-colors ${
                      selected
                        ? "border-l-2 border-l-accent bg-accent-soft"
                        : "border-l-2 border-l-transparent hover:bg-bg-2"
                    }`}
                  >
                    <td
                      className={`px-3 py-2.5 font-mono text-[13px] ${selected ? "text-accent" : "text-fg-1"}`}
                    >
                      {r.circuit ?? "—"}
                    </td>
                    <td className="px-3 py-2.5 font-mono text-xs text-fg-2">{r.posLabel}</td>
                    <td className="px-3 py-2.5 font-mono text-[13px] text-fg-1">{r.ampLabel}A</td>
                    <td className="px-3 py-2.5 text-[13px] text-fg-2">{r.poleLabel}</td>
                    <td className="px-3 py-2.5">
                      <ConfBar confidence={r.confidence} />
                    </td>
                    <td className="px-3 py-2.5 font-mono text-xs text-fg-3">{r.srcLabel}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Detail region */}
      <div className="flex min-h-0 flex-1 flex-col overflow-auto p-4">
        {selectedRow !== null ? (
          <RawJsonPanel row={selectedRow} invocation={invocation} />
        ) : (
          <IdentityCard identity={identity} lowConfCount={rows.filter((r) => r.confidence < 0.7).length} />
        )}
      </div>
    </aside>
  );
}

function SortHeader({
  label,
  k,
  sort,
  onSort,
  mono = false,
}: {
  readonly label: string;
  readonly k: SortKey;
  readonly sort: Sort;
  readonly onSort: (key: SortKey) => void;
  readonly mono?: boolean;
}) {
  const active = sort.key === k;
  return (
    <th
      className={`sticky top-0 cursor-pointer select-none bg-bg-1 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-fg-3 ${mono ? "font-mono" : ""}`}
      onClick={() => onSort(k)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active ? (
          <span className="text-accent">{sort.dir === 1 ? "▲" : "▼"}</span>
        ) : null}
      </span>
    </th>
  );
}

function ConfBar({ confidence }: { readonly confidence: number }) {
  const tone = confidenceTone(confidence);
  const color =
    tone === "success" ? "var(--success)" : tone === "warning" ? "var(--warning)" : "var(--danger)";
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="block h-[5px] w-[38px] overflow-hidden rounded-pill bg-bg-5">
        <span
          className="block h-full rounded-pill"
          style={{ width: `${Math.round(Math.min(1, Math.max(0, confidence)) * 100)}%`, background: color }}
        />
      </span>
      <span className="font-mono text-[11px] text-fg-2">{confidence.toFixed(2)}</span>
    </span>
  );
}

function RawJsonPanel({
  row,
  invocation,
}: {
  readonly row: BreakerRow;
  readonly invocation: InvocationRecord;
}) {
  const json = useMemo(
    () => suggestionRawJson(invocation.rawResponse, row.suggestionId),
    [invocation.rawResponse, row.suggestionId],
  );
  // Legacy tile provenance, when a pre-union row carried it; the union read
  // path has no tiles, so this is `null` and the source-photo line shows.
  const tileTail = row.tileKey?.split("::").slice(-2).join("::") ?? null;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-lg font-bold text-fg-1">
          #{row.circuit ?? "?"}
        </span>
        <span className="font-mono text-[15px] text-accent">{row.ampLabel}A</span>
        <Badge variant={row.pole === "single" ? "neutral" : "accent"}>{row.poleLabel}</Badge>
        {row.confidence < 1 ? (
          <Badge variant="warning">conf {row.confidence.toFixed(2)}</Badge>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-1.5">
        {row.protectionTypes.length > 0 ? (
          row.protectionTypes.map((p) => (
            <span
              key={p}
              className="rounded-sm border border-border-1 bg-bg-4 px-1.5 py-0.5 font-mono text-[11px] text-fg-2"
            >
              {p}
            </span>
          ))
        ) : (
          <span className="text-xs text-fg-3">no protection flags</span>
        )}
      </div>

      {row.manufactureDate !== null ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-fg-3">made</span>
          <Badge variant="neutral">{row.manufactureDate.label}</Badge>
          {row.manufactureDate.raw !== null ? (
            <span className="font-mono text-[11px] text-fg-3">
              stamp “{row.manufactureDate.raw}”
            </span>
          ) : null}
          {row.manufactureDate.ambiguous ? (
            <Badge variant="warning">year cohort-resolved</Badge>
          ) : null}
          {row.manufactureDate.confidence < 0.7 ? (
            <Badge variant="warning">
              read {row.manufactureDate.confidence.toFixed(2)}
            </Badge>
          ) : null}
        </div>
      ) : null}

      {tileTail !== null ? (
        <p className="font-mono text-[11px] text-fg-3">
          read from {tileTail}
          {row.sourcePhotoId !== null ? ` · region on ${row.sourcePhotoId}` : ""}
        </p>
      ) : row.sourcePhotoId !== null ? (
        <p className="font-mono text-[11px] text-fg-3">
          read from {row.sourcePhotoId}
        </p>
      ) : (
        <p className="text-[11px] text-fg-3">
          no source photo recorded for this read.
        </p>
      )}

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-border-1 bg-bg-0">
        <JsonHighlight text={json.text} className="flex-1 px-3 py-2 text-[11.5px]" />
      </div>
    </div>
  );
}

function IdentityCard({
  identity,
  lowConfCount,
}: {
  readonly identity: PanelIdentity | null;
  readonly lowConfCount: number;
}) {
  if (identity === null) {
    return (
      <div className="rounded-lg border border-border-1 bg-bg-2 p-4">
        <h3 className="text-sm font-semibold text-fg-1">Panel identity</h3>
        <p className="mt-1 text-[13px] text-fg-3">
          The identity pass returned no panel metadata for this invocation.
          Select a read above to inspect it.
        </p>
      </div>
    );
  }

  // KAN-81 — the badged identity fields carry an independent confidence; the
  // third tuple slot names that field so the row can show its own "low" badge
  // (Series / Main are not per-field badged, so they have no field key).
  const rows: ReadonlyArray<
    [string, string, PanelIdentityConfidenceField?]
  > = [
    ["Manufacturer", identity.manufacturer ?? "—", "manufacturer"],
    ["Series", identity.series ?? "—"],
    ["Model", identity.model ?? "—", "model"],
    ["Main", identity.mainAmperage !== undefined ? `${identity.mainAmperage}A` : "—"],
    [
      "Total slots",
      identity.totalSlots !== undefined ? String(identity.totalSlots) : "—",
      "totalSlots",
    ],
    ["Numbering", identity.numberingScheme ?? "—", "numberingScheme"],
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-lg border border-border-1 bg-bg-2 p-4">
        <div className="mb-3 flex items-center gap-2">
          <span className="text-accent-2">
            <PanelIcon />
          </span>
          <h3 className="text-sm font-semibold text-fg-1">Panel identity</h3>
          <span className="flex-1" />
          <Badge variant="accent">conf {identity.confidence.toFixed(2)}</Badge>
        </div>
        <dl className="flex flex-col gap-2">
          {rows.map(([label, value, field]) => {
            const conf =
              field !== undefined ? identityFieldConfidence(identity, field) : null;
            const low = conf !== null && conf < 0.7;
            return (
              <div key={label} className="flex items-baseline justify-between gap-3">
                <dt className="text-[13px] text-fg-3">{label}</dt>
                <dd className="m-0 flex items-baseline gap-2 text-right font-mono text-[13px] text-fg-1">
                  {low ? (
                    <Badge variant="warning">low {conf.toFixed(2)}</Badge>
                  ) : null}
                  <span>{value}</span>
                </dd>
              </div>
            );
          })}
        </dl>
      </div>

      {lowConfCount > 0 ? (
        <div className="rounded-lg border border-warning/30 bg-warning-soft p-3">
          <span className="text-[12.5px] text-warning">
            <strong>{lowConfCount}</strong> read{lowConfCount === 1 ? "" : "s"} below
            the 0.70 review threshold — filter by <span className="font-mono">Low conf</span>{" "}
            to triage.
          </span>
        </div>
      ) : null}
    </div>
  );
}

function ParseFailedPane({ invocation }: { readonly invocation: InvocationRecord }) {
  const raw = invocation.rawResponse;
  const text =
    raw === null || raw === undefined
      ? "(no response captured)"
      : typeof raw === "string"
        ? raw
        : JSON.stringify(raw, null, 2);
  return (
    <aside
      aria-label="reads-panel"
      className="flex h-full min-h-0 flex-col gap-3 border-l border-border-1 bg-bg-1 p-4"
    >
      <Badge variant="danger">Parse failed</Badge>
      <h3 className="text-base font-semibold text-fg-1">Model response didn't parse</h3>
      <p className="text-[13px] text-fg-3">
        No suggestions were produced. The verbatim response is below — diagnose
        whether the model returned malformed output or hit a token limit.
      </p>
      <pre className="min-h-0 flex-1 overflow-auto whitespace-pre-wrap rounded-lg border border-danger/30 bg-bg-0 p-3 font-mono text-[11.5px] text-danger">
        {text}
      </pre>
    </aside>
  );
}

function FilterIcon() {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden className="text-fg-3">
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}

function PanelIcon() {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="4" y="2" width="16" height="20" rx="2" />
      <line x1="9" y1="6" x2="9" y2="18" />
      <line x1="15" y1="6" x2="15" y2="18" />
    </svg>
  );
}
